/**
 * What the app keeps for a signed-in person beyond the auth keys, and how it
 * goes when the session ends.
 *
 * Every native end of a session runs wipeSessionData(): the local-first wipe
 * (sign-out, idle expiry, the app lock's escape and its fifth failure) and the
 * Sign out button's own clean-up. It once ran only on the button, so data an
 * idle expiry left behind outlived the session (Codex on #1134).
 *
 * A module that keeps such data in memory, or may have a write in flight,
 * registers a `forget` here that stops both. Every `forget` runs before
 * secure storage is cleared, so nothing lands after the clear and puts the
 * data back. A module never loaded has nothing to forget: its stored data
 * goes with the store.
 */
import type { KeyValueStore } from '@/platform/types';
import { clearBookingDraft, flushBookingDraft } from '@/lib/bookingPersistence';
import { logger } from '@/lib/logger';
import { wipePerUserBrowserStorage } from '@/lib/perUserBrowserStorage';
import { clearSharedFiles } from '@/lib/sharedFiles';
import { underSessionStoreLock } from '@/lib/sessionStoreLock';

type Forget = () => Promise<void> | void;

const forgetters = new Set<Forget>();

/** Registers a module's forget; the returned function removes it. */
export function onSessionWipe(forget: Forget): () => void {
  forgetters.add(forget);
  return () => {
    forgetters.delete(forget);
  };
}

export interface WipeOptions {
  /**
   * Whether the session this wipe is for is still the one that ended. A wipe
   * that runs in the background (the SIGNED_OUT listener) passes it: if a new
   * sign-in has stored a session meanwhile, the wipe stops rather than clear
   * that session and its account's data (Codex on #1134 and #1136). A check
   * that fails counts as "no". It is asked before anything is dropped, again
   * before the modules forget, and again before the clear.
   * The whole wipe holds the session store lock, so no sign-in's write lands
   * between a check and what follows it (Codex on #1134 and #1137).
   */
  stillEnded?: () => Promise<boolean>;
  /**
   * Keys of a sign-in begun since the session ended, read under the lock just
   * before the store is emptied: those stay, and everything else of the
   * ended account goes (Codex on #1136). A sign-in by link stores its PKCE
   * verifier before any session; stopping the whole wipe for it would leave
   * the ended account's data to the next account. A read that fails stops
   * the wipe before anything is emptied, as a failed check does: emptying
   * could delete a sign-in under way (Codex on #1136).
   */
  keepKeys?: () => Promise<readonly string[]>;
}

async function mayContinue(opts: WipeOptions): Promise<boolean> {
  if (!opts.stillEnded) return true;
  try {
    if (await opts.stillEnded()) return true;
    logger.info('[session-wipe] a new session started; the old session\'s wipe stops');
  } catch (e) {
    logger.warn('[session-wipe] could not tell whether a new session started; the wipe stops', e);
  }
  return false;
}

/** Forget what is held in memory, then empty the secure store. App only; never throws. */
export async function wipeSessionData(secure: KeyValueStore, opts: WipeOptions = {}): Promise<void> {
  return underSessionStoreLock(() => wipe(secure, opts));
}

async function wipe(secure: KeyValueStore, opts: WipeOptions): Promise<void> {
  if (!(await mayContinue(opts))) return;
  // The WebView's own storage: keys named after this person (the feedback
  // gate, the onboarding step, ...), and what the share sheet was given.
  wipePerUserBrowserStorage();
  const sharedFiles = clearSharedFiles();
  // The booking draft is read from an in-memory copy and written behind it:
  // drop the copy and let its writes settle first, so none lands after the
  // clear and puts the draft back.
  clearBookingDraft();
  await flushBookingDraft().catch((e) => logger.warn('[session-wipe] the booking draft did not settle', e));
  if (!(await mayContinue(opts))) return;
  await Promise.allSettled([
    sharedFiles,
    ...[...forgetters].map(async (forget) => {
      try {
        await forget();
      } catch (e) {
        logger.warn('[session-wipe] a module could not forget its data', e);
      }
    }),
  ]);
  if (!(await mayContinue(opts))) return;
  let keep: readonly string[] = [];
  try {
    keep = (await opts.keepKeys?.()) ?? [];
  } catch (e) {
    logger.warn('[session-wipe] could not tell whether a sign-in is under way; the wipe stops', e);
    return;
  }
  try {
    if (keep.length === 0) {
      await secure.clear();
      return;
    }
    const kept = new Set(keep);
    const results = await Promise.allSettled(
      (await secure.keys()).filter((key) => !kept.has(key)).map((key) => secure.remove(key)),
    );
    if (results.some((r) => r.status === 'rejected')) throw new Error('Some keys could not be removed');
  } catch (e) {
    logger.warn('Failed to wipe the secure session store', e);
  }
}
